import {
  Button,
  Dialog,
  Paragraph,
  Spacer,
  Title
} from '@efast_public/fjd-component-library';
import { ErrorType, ReasonType } from '../shared/error/ErrorCode';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import { ApiRoutes, mandantClient } from '../api/mandant-api';
import { useNotification } from '../shared/utils/notification/useNotification';
import {useProfile} from "../shared/utils/auth/AuthContext";
import {useNavigate} from "react-router-dom";
import {RouteKey} from "../pages/AppRoutes";

interface DeleteUsersAndRolesManagementDeleteDialogProps {
  readonly onClose: () => void;
  readonly isOpen: boolean;
  readonly userId: string;
}

export default function DeleteUsersAndRolesManagementDeleteDialog(
  props: DeleteUsersAndRolesManagementDeleteDialogProps
) {
  const { notifyUserFromRequest, notifyUser } = useNotification();
  const navigate = useNavigate();
  const  queryClient = useQueryClient();
  const { profile, logout } = useProfile();
  const { isPending: isDeleting, mutateAsync: deleteUser } = useMutation({
    mutationFn: (userId: string) =>
      notifyUserFromRequest(
        () => mandantClient.delete(ApiRoutes.user(userId)),
        {
          successMessage: 'Benutzer:in wurde erfolgreich gelöscht',
          loadingMessage: 'Benutzer:in wird gelöscht...',
          errorMessage: 'Benutzer:in konnte nicht gelöscht werden',
          errorIdHandler: {
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_USER_CANNOT_BE_DELETED]: (
              reasons
            ) => {
              const hasCasesReason = reasons?.some(
                (r) =>
                  r.type === ReasonType.BEHOERDENCLIENT_REASONS_USER_HAS_CASES
              );
              const isOnlyAdminReason = reasons?.some(
                (r) =>
                  r.type ===
                  ReasonType.BEHOERDENCLIENT_REASONS_USER_IS_ONLY_ADMIN
              );

              if (hasCasesReason && isOnlyAdminReason) {
                notifyUser({
                  intent: 'error',
                  title:
                    'Benutzer:innen können nicht gelöscht werden, solange sie Anträge bearbeiten.'
                });
                notifyUser({
                  intent: 'error',
                  title:
                    'Benutzer:innen können nicht gelöscht werden, da mindestens ein Administrator vergeben sein muss.'
                });
              } else if (hasCasesReason) {
                notifyUser({
                  intent: 'error',
                  title:
                    'Benutzer:innen können nicht gelöscht werden, solange sie Anträge bearbeiten.'
                });
              } else if (isOnlyAdminReason) {
                notifyUser({
                  intent: 'error',
                  title:
                    'Benutzer:innen können nicht gelöscht werden, da mindestens ein Administrator vergeben sein muss.'
                });
              } else {
                notifyUser({
                  intent: 'error',
                  title: 'Benutzer:in konnte nicht geloescht werden'
                });
              }
            }
          }
        }
      ),
    onSuccess: async () => {
      if (profile?.id === props.userId) {
        logout();
      } else {
        props.onClose();
        navigate(RouteKey.User)
        queryClient.invalidateQueries({queryKey: [ApiRoutes.users()]})
      }
    }
  });

  return (
    <Dialog
      open={props.isOpen}
      header={
        <Title data-testid={props.userId} level={1} visualLevel={6}>
          Wollen Sie diese:n Benutzer:in wirklich unwiderruflich löschen?
        </Title>
      }
      onClose={props.onClose}
      actions={
        <>
          <Button size="sm" variant="primary-text" onClick={props.onClose}>
            Abbrechen
          </Button>

          <Button
            size="sm"
            onClick={() => deleteUser(props.userId).catch(() => {})}
          >
            {isDeleting ? <Spinner size="sm" /> : 'Benutzer:in löschen'}
          </Button>
        </>
      }
    >
      <Paragraph data-testid={`modalContentText-${props.userId}`}>
        Diese:r Benutzer:in hat danach keine Zugriffsrechte mehr.
      </Paragraph>
    </Dialog>
  );
}
